import * as styles from "./Avatar.css";

import { clsx } from "clsx";
import type { JSX } from "react";

import { Avatar as BaseAvatar } from "@base-ui/react/avatar";
import { Icon } from "@frontend/components/atoms/Icon";

import type { AvatarKind, AvatarProps, AvatarRadius } from "./Avatar.types";
import { useAvatarGroupContext } from "./AvatarGroup.context";

const kindRadius: Record<AvatarKind, AvatarRadius> = {
  person: "full",
  organization: "md",
};

export function Avatar({
  variant,
  size,
  radius,
  bordered,
  invalid,
  success,
  kind = "person",
  status,
  className,
  style,
  fallbackDelay,
  url,
  onLoadingStatusChange,
  fallback,
  ref,
  editMode,
  "aria-label": ariaLabel,
}: AvatarProps): JSX.Element {
  const group = useAvatarGroupContext();

  return (
    <BaseAvatar.Root
      data-slot="avatar"
      data-kind={kind}
      className={clsx(
        styles.avatar({
          variant,
          size: size ?? group?.size,
          radius: radius ?? group?.radius ?? kindRadius[kind],
          bordered,
          invalid,
          success,
          editMode,
        }),
        className,
      )}
      style={style}
      ref={ref}
      aria-label={ariaLabel}
    >
      {url ? (
        <BaseAvatar.Image
          src={url}
          className={styles.image}
          onLoadingStatusChange={onLoadingStatusChange}
        />
      ) : null}
      <BaseAvatar.Fallback className={styles.fallback} delay={fallbackDelay}>
        {fallback ?? (
          <Icon
            name={kind === "organization" ? "Factory" : "UserRound"}
            size="1.25em"
          />
        )}
      </BaseAvatar.Fallback>
      {editMode && (
        <span className={styles.editOverlay}>
          <Icon name="Upload" size="1em" />
        </span>
      )}
      {status && (
        <span
          className={styles.status({ status })}
          data-status={status}
          aria-hidden
        />
      )}
    </BaseAvatar.Root>
  );
}
